import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Modal, Row, Button } from "react-bootstrap";
import { Form, Input, InputNumber, Radio, Select, Spin } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGift } from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import classNames from "classnames";
import { debounce } from "lodash";
import { useToasts } from "../../contexts/toasts";
import { User } from "../../api/User";

type Target = "all" | "selected";

type SelectedUser = { label: string; value: string };

type FormValues = {
  target: Target;
  users?: SelectedUser[];
  points: number;
  title: string;
  message: string;
};

export default function AssignPoints() {
  const { showToast } = useToasts();
  const [form] = Form.useForm<FormValues>();
  const [sending, setSending] = useState(false);
  const [options, setOptions] = useState<SelectedUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [confirmValues, setConfirmValues] = useState<FormValues>();
  const target = Form.useWatch("target", form) as Target | undefined;
  const lastQuery = useRef("");

  // Ricerca utenti per nome/cognome sull'endpoint paginato del Superadmin.
  const searchUsers = useCallback((text: string) => {
    const query = text.trim();
    lastQuery.current = query;
    if (query.length < 2) {
      setOptions([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const params = new URLSearchParams();
    params.append("page", "1");
    params.append("limit", "20");
    params.append("freeText", query);
    User.getAllPaginated(params).then(
      ({ data }) => {
        if (lastQuery.current !== query) return;
        setOptions(
          data.items.map((item) => ({
            value: item.userId,
            label: `${item.user.name} ${item.user.familyName}${
              item.email ? ` (${item.email})` : ""
            }`,
          }))
        );
        setSearching(false);
      },
      () => {
        if (lastQuery.current !== query) return;
        setOptions([]);
        setSearching(false);
      }
    );
  }, []);

  const debouncedSearch = useMemo(() => debounce(searchUsers, 400), [searchUsers]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleSubmit = useCallback((values: FormValues) => {
    setConfirmValues(values);
  }, []);

  const confirmSend = useCallback(() => {
    if (!confirmValues) return;
    setSending(true);
    User.assignPoints({
      points: confirmValues.points,
      target: confirmValues.target,
      userIds:
        confirmValues.target === "selected"
          ? confirmValues.users?.map((u) => u.value)
          : undefined,
      title: confirmValues.title.trim(),
      message: confirmValues.message.trim(),
    }).then(
      ({ data }) => {
        setSending(false);
        setConfirmValues(undefined);
        form.resetFields();
        setOptions([]);
        showToast(
          "assign-points-success",
          `${data.points} Get Move Points assegnati a ${data.recipients} ${
            data.recipients === 1 ? "utente" : "utenti"
          } (${data.pushed} notifiche push inviate).`,
          "",
          "success"
        );
      },
      (err) => {
        setSending(false);
        showToast(
          "assign-points-error",
          err?.response?.data?.message ||
            "Errore nell'assegnazione dei punti, per favore riprova.",
          "",
          "danger"
        );
      }
    );
  }, [confirmValues, form, showToast]);

  return (
    <Container fluid="md" className="mt-5 mb-5">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ target: "all", points: 1 }}
      >
        <Row className="mb-2 align-items-center">
          <Col>
            <h4 className="mb-0">Assegna Get Move Points</h4>
          </Col>
          <Col xs={6} md={4} className="text-end">
            <button
              type="submit"
              disabled={sending}
              className={classNames("button-primary", {
                "button-disabled": sending,
              })}
            >
              {sending ? (
                <TailSpin color="#FFF" width={15} height={15} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faGift} />
                  <span className="d-none d-sm-inline ms-2">Assegna</span>
                </>
              )}
            </button>
          </Col>
        </Row>
        <hr />

        <Row>
          <Col xs={12} md={8}>
            <p className="text-muted">
              Accredita una quantità di Get Move Points a tutti gli utenti
              dell'app oppure a una selezione. Ogni destinatario riceve una
              notifica in-app e push con il messaggio che scrivi qui sotto.
            </p>

            <Form.Item
              label="Destinatari"
              name="target"
              rules={[{ required: true, message: "Seleziona i destinatari" }]}
            >
              <Radio.Group>
                <Radio value="all">Tutti gli utenti registrati su Get Move</Radio>
                <Radio value="selected">Solo gli utenti selezionati</Radio>
              </Radio.Group>
            </Form.Item>

            {target === "selected" && (
              <Form.Item
                label="Utenti"
                name="users"
                rules={[
                  {
                    validator: async (_, value: SelectedUser[]) => {
                      if (!value || value.length === 0) {
                        return Promise.reject(new Error("Seleziona almeno un utente"));
                      }
                    },
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  labelInValue
                  showSearch
                  filterOption={false}
                  placeholder="Cerca per nome o cognome (min. 2 caratteri)..."
                  onSearch={debouncedSearch}
                  notFoundContent={
                    searching ? <Spin size="small" /> : "Nessun utente trovato"
                  }
                  options={options}
                  getPopupContainer={(trigger) => trigger.parentNode}
                />
              </Form.Item>
            )}

            <Form.Item
              label="Punti da assegnare a ciascun utente"
              name="points"
              rules={[
                { required: true, message: "Inserisci il numero di punti" },
                { type: "number", min: 1, message: "Inserisci un numero di punti valido" },
              ]}
            >
              <InputNumber className="w-100" min={1} step={1} precision={0} />
            </Form.Item>

            <Form.Item
              label="Titolo della notifica"
              name="title"
              rules={[
                { required: true, message: "Inserisci un titolo" },
                { whitespace: true, message: "Inserisci un titolo valido" },
              ]}
            >
              <Input placeholder="Es. Un regalo per te 🎁" maxLength={100} />
            </Form.Item>

            <Form.Item
              label="Messaggio"
              name="message"
              rules={[
                { required: true, message: "Inserisci un messaggio" },
                { whitespace: true, message: "Inserisci un messaggio valido" },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Es. Ti abbiamo accreditato 5 Get Move Points per ringraziarti..."
                maxLength={500}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Modal show={!!confirmValues} onHide={() => !sending && setConfirmValues(undefined)}>
        <Modal.Header closeButton>
          <Modal.Title>Conferma assegnazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Stai per assegnare <strong>{confirmValues?.points}</strong> Get Move
          Points{" "}
          {confirmValues?.target === "all" ? (
            <strong>a tutti gli utenti dell'app</strong>
          ) : (
            <>
              a <strong>{confirmValues?.users?.length}</strong>{" "}
              {confirmValues?.users?.length === 1 ? "utente" : "utenti"}
            </>
          )}
          . L'operazione non si può annullare.
          {confirmValues?.target === "selected" && (
            <ul className="mt-2 mb-0" style={{ fontSize: "0.9rem" }}>
              {confirmValues.users?.map((u) => (
                <li key={u.value}>{u.label}</li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            disabled={sending}
            onClick={() => setConfirmValues(undefined)}
          >
            Annulla
          </Button>
          <Button variant="success" size="sm" disabled={sending} onClick={confirmSend}>
            {sending ? (
              <TailSpin width={20} height={20} />
            ) : (
              <>
                <FontAwesomeIcon icon={faGift} />
                <span> Conferma</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
